/**
 * Anmeldung und Sitzung (KONZEPT.md §4).
 *
 * „Anmeldung mit Benutzername und Passwort oder kurzer PIN; die Sitzung bleibt
 * auf dem Geraet bestehen." Das zweite ist wichtiger als es klingt: Ein Handy,
 * das sich nach acht Stunden abmeldet, ist an der Einsatzstelle wertlos --
 * dort gibt es kein Netz, um sich neu anzumelden.
 */
import { z } from "zod";
import { Id, KurzText } from "./basis.js";
import { Rolle } from "./enums.js";
import { Benutzer } from "./stammdaten.js";
/**
 * Das Geheimnis eines Benutzers beim Anmelden: Passwort **oder** PIN.
 *
 * Bewusst ohne Regeln ausser "nicht leer" (ADR 0024). Die Regeln gelten beim
 * Festlegen, nicht beim Anmelden -- sonst sperrt eine spaeter verschaerfte
 * Regel Leute aus, und an der Einsatzstelle ohne Netz ist das nicht mehr
 * gutzumachen.
 */
export const Geheimnis = z.string().min(1).max(200);
/** Kuerzer darf ein neues Kennwort oder eine neue PIN nicht sein (ADR 0024). */
export const GEHEIMNIS_MINDESTLAENGE = 6;
/**
 * Was an einem neuen Kennwort oder einer neuen PIN auszusetzen ist, oder
 * `null`, wenn es passt (ADR 0024).
 *
 * Eine PIN ist kurz, damit sie sich mit Handschuhen tippen laesst -- deshalb
 * keine Pflicht zu Grossbuchstaben oder Sonderzeichen. Abgelehnt wird nur,
 * was jeder zuerst probiert: ein Zeichen immer wieder, eine einfache Folge,
 * ein kurzes Muster in Wiederholung und der eigene Benutzername.
 *
 * Server und Oberflaeche rufen dieselbe Funktion auf. Die Meldung geht
 * unveraendert an den Benutzer.
 */
export function geheimnisMangel(geheimnis, benutzername) {
    // Wie beim Hashen normalisiert, dazu klein: "aaaAAA" ist genauso schwach
    // wie "aaaaaa". Array.from zaehlt Zeichen, nicht UTF-16-Einheiten.
    const text = geheimnis.normalize("NFKC").toLowerCase();
    const zeichen = Array.from(text);
    const laenge = zeichen.length;
    if (laenge < GEHEIMNIS_MINDESTLAENGE) {
        return `Mindestens ${String(GEHEIMNIS_MINDESTLAENGE)} Zeichen`;
    }
    if (new Set(zeichen).size === 1) {
        return "Nur ein Zeichen immer wieder ist zu einfach";
    }
    const punkte = zeichen.map((z) => z.codePointAt(0) ?? 0);
    const schritte = punkte.slice(1).map((p, i) => p - (punkte[i] ?? 0));
    if (schritte.every((s) => s === 1) || schritte.every((s) => s === -1)) {
        return "Eine einfache Folge wie 123456 oder abcdef ist zu einfach";
    }
    // Periode p: jedes Zeichen gleicht dem, das p Stellen vorher steht.
    // Hoechstens die halbe Laenge, sonst wiederholt sich nichts.
    for (let p = 2; p <= laenge / 2; p++) {
        if (zeichen.every((z, i) => i < p || z === zeichen[i - p])) {
            return "Ein Muster, das sich nur wiederholt, wie 121212, ist zu einfach";
        }
    }
    if (benutzername !== undefined && text === benutzername.trim().normalize("NFKC").toLowerCase()) {
        return "Der Benutzername taugt nicht als Kennwort";
    }
    return null;
}
/**
 * Ein neues Kennwort oder eine neue PIN -- mit den Regeln aus ADR 0024.
 *
 * Den Benutzernamen kennt das Schema nicht; den prueft der Server, der ihn
 * bei jeder Aenderung zur Hand hat.
 */
export const NeuesGeheimnis = z
    .string()
    .max(200)
    .superRefine((geheimnis, ctx) => {
    const mangel = geheimnisMangel(geheimnis);
    if (mangel !== null)
        ctx.addIssue({ code: "custom", message: mangel });
});
export const Anmeldung = z.object({
    benutzername: z.string().trim().min(1).max(60),
    geheimnis: Geheimnis,
    /**
     * Kennung des Geraets. Wandert in jede Buchung und entscheidet
     * Gleichstaende bei der Konfliktaufloesung.
     */
    geraet: KurzText.default("unbekannt"),
});
export const Sitzung = z.object({
    /** Wird als `Authorization: Bearer <token>` mitgeschickt. */
    token: z.string(),
    gueltig_bis: z.iso.datetime({ offset: true }),
    benutzer: Benutzer,
});
/** Was im Token steht. Bewusst wenig -- alles Weitere kommt aus der Datenbank. */
export const TokenInhalt = z.object({
    benutzer_id: Id,
    rolle: Rolle,
    geraet: z.string(),
});
export const BenutzerAnlegen = z.object({
    benutzername: Benutzer.shape.benutzername,
    anzeigename: KurzText,
    rolle: Rolle.default("benutzer"),
    geheimnis: NeuesGeheimnis,
});
export const BenutzerAendern = z.object({
    anzeigename: KurzText.optional(),
    rolle: Rolle.optional(),
    /**
     * Benutzer werden nie geloescht, nur deaktiviert -- so wie Lagerorte.
     * Ihre Buchungen sollen auch nach dem Austritt zuordenbar bleiben.
     */
    aktiv: z.boolean().optional(),
    geheimnis: NeuesGeheimnis.optional(),
});
/**
 * Das eigene Konto aendern -- jeder fuer sich, ohne Administrator.
 *
 * Ein neues Kennwort braucht das bisherige. Sonst genuegte ein Handy, das
 * kurz unbeaufsichtigt auf dem Tisch liegt, um jemanden dauerhaft
 * auszusperren -- die Anmeldung bleibt auf dem Geraet ja bestehen.
 */
export const EigenesKontoAendern = z
    .object({
    anzeigename: KurzText.optional(),
    geheimnis_bisher: z.string().max(200).optional(),
    geheimnis_neu: NeuesGeheimnis.optional(),
})
    .refine((k) => k.geheimnis_neu === undefined || k.geheimnis_bisher !== undefined, {
    message: "Fuer ein neues Kennwort braucht es das bisherige",
    path: ["geheimnis_bisher"],
});
//# sourceMappingURL=anmeldung.js.map